import { useMemo } from "react";
import { THEME_TOKEN_META, isValidColorString } from "../meta";

interface Props {
  tokens: Record<string, string>;
  onChange: (key: string, value: string) => void;
  onError?: (msg: string) => void;
}

export function ColorTokensEditor({ tokens, onChange, onError }: Props) {
  const grouped = useMemo(() => {
    const map: Record<string, typeof THEME_TOKEN_META> = {};
    THEME_TOKEN_META.forEach((m) => {
      if (!map[m.group]) map[m.group] = [];
      map[m.group].push(m);
    });
    return Object.entries(map);
  }, []);

  return (
    <div className="flex flex-col gap-3">
      {grouped.map(([groupName, metas]) => (
        <div key={groupName}>
          <h4 className="text-2xs uppercase tracking-wider text-fg-subtle font-semibold mt-2 mb-2">
            {groupName}
          </h4>
          <div className="flex flex-col gap-2">
            {metas.map((meta) => {
              const value = tokens[meta.key] || "";
              const pickerValue = /^#[0-9a-f]{6}$/i.test(value) ? value : "#000000";
              return (
                <div
                  key={meta.key}
                  className="grid items-center gap-2.5"
                  style={{ gridTemplateColumns: "40px 1fr 120px" }}
                >
                  <input
                    type="color"
                    value={pickerValue}
                    onChange={(e) => onChange(meta.key, e.target.value)}
                    className="w-10 h-8 rounded border border-border p-0 bg-transparent cursor-pointer"
                    aria-label={`Color picker para ${meta.label}`}
                  />
                  <div className="min-w-0">
                    <div className="text-xs font-medium text-fg">{meta.label}</div>
                    <div className="text-2xs text-fg-subtle font-mono">{meta.hint}</div>
                  </div>
                  <input
                    type="text"
                    defaultValue={value}
                    key={value}
                    onBlur={(e) => {
                      const v = e.target.value.trim();
                      if (v === value) return;
                      if (!isValidColorString(v)) {
                        e.target.style.borderColor = "var(--nx-danger)";
                        onError?.(`Valor inválido para ${meta.label}: "${v}"`);
                        return;
                      }
                      e.target.style.borderColor = "";
                      onChange(meta.key, v);
                    }}
                    className="nx-input text-2xs font-mono text-right"
                    placeholder="#RRGGBB"
                  />
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
